import { useEffect, useState } from "react";
import { dataApi, authedUrl } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Primitives";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Crown, Award, Medal, Sparkles, Star } from "lucide-react";

const METRICS = [
  { value: "productivity_score", label: "Productivity" },
  { value: "attendance_score", label: "Attendance" },
  { value: "task_completion_rate", label: "Task Completion" },
  { value: "code_quality_score", label: "Code Quality" },
  { value: "review_score", label: "Reviews" },
  { value: "velocity", label: "Velocity" },
];

const TIERS = [
  {
    // 1st place
    tierClass: "tier-1",
    ringClass: "prism-ring",
    ribbon: "Champion",
    label: "1st",
    Icon: Crown,
    height: "h-52 md:h-60",
    order: "order-2",
    scale: "md:scale-110",
    avatarSize: "h-24 w-24 md:h-28 md:w-28",
    delay: "0.15s",
  },
  {
    // 2nd place
    tierClass: "tier-2",
    ringClass: "prism-ring-sm prism-ring-2",
    ribbon: "Runner-up",
    label: "2nd",
    Icon: Award,
    height: "h-40 md:h-44",
    order: "order-1",
    scale: "",
    avatarSize: "h-20 w-20",
    delay: "0.35s",
  },
  {
    // 3rd place
    tierClass: "tier-3",
    ringClass: "prism-ring-sm prism-ring-3",
    ribbon: "Third",
    label: "3rd",
    Icon: Medal,
    height: "h-32 md:h-36",
    order: "order-3",
    scale: "",
    avatarSize: "h-20 w-20",
    delay: "0.55s",
  },
];

function PodiumCard({ person, tier }) {
  const { tierClass, ringClass, ribbon, label, Icon, height, order, scale, avatarSize, delay } = tier;
  const initials = person.name.split(" ").map((s) => s[0]).slice(0, 2).join("");
  return (
    <div
      className={`${order} ${scale} rise-in flex flex-col items-center relative transition-transform`}
      style={{ animationDelay: delay }}
      data-testid={`podium-${label.toLowerCase()}`}
    >
      {/* Floating icon above avatar */}
      <div className="relative mb-3">
        {label === "1st" && (
          <>
            <Sparkles className="w-4 h-4 text-yellow-300 absolute -top-4 -left-6 twinkle" />
            <Star className="w-3 h-3 text-fuchsia-300 absolute -top-2 -right-6 twinkle" style={{ animationDelay: "0.6s" }} />
            <Star className="w-3 h-3 text-cyan-300 absolute top-6 -right-8 twinkle" style={{ animationDelay: "1.1s" }} />
          </>
        )}
        <div className="float-y">
          <Icon
            className={`${label === "1st" ? "w-9 h-9" : "w-7 h-7"} drop-shadow-lg`}
            style={{
              color:
                label === "1st" ? "#FFC93A" :
                label === "2nd" ? "#9CA3AF" :
                "#F97316",
              filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.25))",
            }}
          />
        </div>
      </div>

      {/* Avatar with prismatic ring */}
      <div className={`${ringClass}`}>
        <Avatar className={`${avatarSize} ring-4 ring-background`}>
          <AvatarImage src={authedUrl(person.avatar_url)} />
          <AvatarFallback className="text-lg font-semibold">{initials}</AvatarFallback>
        </Avatar>
      </div>

      {/* Name + department */}
      <div className="mt-3 text-center px-2">
        <div className={`font-semibold ${label === "1st" ? "text-base md:text-lg" : "text-sm"} truncate max-w-[10rem]`}>
          {person.name}
        </div>
        <div className="text-xs text-muted-foreground truncate max-w-[10rem]">{person.department || "—"}</div>
      </div>

      {/* Pillar */}
      <div className={`relative w-full mt-4 ${height} rounded-t-2xl tier-sheen ${tierClass} shadow-[0_20px_50px_-20px_rgba(0,0,0,0.5)] overflow-hidden`}>
        {/* Ribbon */}
        <span className="ribbon">{ribbon}</span>

        {/* Rank numeral watermark */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
          aria-hidden
        >
          <span
            className="text-[7rem] md:text-[9rem] font-black leading-none"
            style={{ color: "rgba(255,255,255,0.10)", letterSpacing: "-0.05em" }}
          >
            {label[0]}
          </span>
        </div>

        {/* Score */}
        <div className="relative z-10 h-full flex flex-col items-center justify-end pb-5 text-white">
          <div className="text-[10px] uppercase tracking-[0.22em] opacity-90">Score</div>
          <div className={`number ${label === "1st" ? "text-4xl md:text-5xl" : "text-3xl"} font-bold drop-shadow leading-none`}>
            {Number(person.score).toFixed(1)}
          </div>
          <div className="mt-2 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/25 backdrop-blur-sm border border-white/30">
            Rank #{person.rank}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Leaderboards() {
  const [metric, setMetric] = useState("productivity_score");
  const [data, setData] = useState(null);

  useEffect(() => {
    dataApi.leaderboard(metric).then((r) => setData(r.data));
  }, [metric]);

  const top3 = data?.leaderboard?.slice(0, 3) || [];

  return (
    <div className="space-y-6">
      <PageHeader
        testid="leaderboards-header"
        title="Leaderboards"
        subtitle="Recognise top performers and drive gamification"
        actions={
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-56" data-testid="lb-metric"><SelectValue /></SelectTrigger>
            <SelectContent>
              {METRICS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />

      {data && (
        <>
          {/* Prismatic Podium Stage */}
          <div className="podium-stage border border-border/40" data-testid="podium-stage">
            <div className="podium-beams" aria-hidden>
              <span /><span /><span /><span /><span />
            </div>

            <div className="grid grid-cols-3 gap-3 md:gap-6 items-end max-w-4xl mx-auto">
              {top3.map((p, i) => (
                <PodiumCard key={p.id} person={p} tier={TIERS[i]} />
              ))}
            </div>

            {/* Metric caption */}
            <div className="mt-6 text-center">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background/70 backdrop-blur border border-border/60 text-xs text-muted-foreground">
                <Sparkles className="w-3 h-3 text-primary" />
                Ranking by{" "}
                <span className="font-semibold text-foreground">
                  {METRICS.find((m) => m.value === metric)?.label}
                </span>
              </span>
            </div>
          </div>

          <SectionCard title="Full Rankings">
            <div className="space-y-1">
              {data.leaderboard.slice(3, 30).map((p) => (
                <div key={p.id} data-testid={`lb-row-${p.rank}`} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0">
                  <div className="number w-8 text-center text-sm text-muted-foreground">#{p.rank}</div>
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={authedUrl(p.avatar_url)} />
                    <AvatarFallback>{p.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{p.department}</div>
                  </div>
                  <Badge variant="secondary" className="number">{p.score}</Badge>
                </div>
              ))}
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}
